import { CodeBlock, Alert, Step, Card, CardGrid } from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Quickstart() {
	return (
		<div className={styles.docsPage}>
			<h1>Quickstart Guide</h1>
			<p className={styles.lead}>
				Get up and running with Ringfort Studio in under 5 minutes. This guide
				will walk you through creating your first game session, loading the game
				client, and placing a test bet.
			</p>

			<Alert variant="info" title="Prerequisites">
				You'll need API credentials to make requests. For this quickstart, we'll
				use the demo sandbox credentials that work out of the box.
			</Alert>

			<Step number={1} title="Get Your Sandbox Credentials">
				<p>
					For testing purposes, you can use the demo sandbox credentials. These
					work immediately without any setup and provide unlimited virtual
					balance.
				</p>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(2, 1fr)',
						gap: 'var(--space-4)',
						marginTop: 'var(--space-4)',
					}}
				>
					<div className={styles.envDetail}>
						<span className={styles.envDetailLabel}>Sandbox Public Key</span>
						<code className={styles.envDetailValue}>pk_test_demopublic001</code>
					</div>
					<div className={styles.envDetail}>
						<span className={styles.envDetailLabel}>Sandbox Secret Key</span>
						<code className={styles.envDetailValue}>sk_test_demosecret001secret001</code>
					</div>
				</div>

				<Alert variant="warning" title="Demo Keys Limitations">
					Demo keys are rate-limited and shared across all users. For production
					development, request your own sandbox keys from our team.
				</Alert>
			</Step>

			<Step number={2} title="Create a Player Session">
				<p>
					Create a game session for your player by calling the sessions
					endpoint. This returns a <code>gameUrl</code> that you'll use to load
					the game client.
				</p>

				<CodeBlock language="bash" title="Request">
					{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_test_demosecret001" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "quickstart_player_001",
    "currency": "USD",
    "walletMode": "integrated",
    "returnUrl": "https://your-site.com/games"
  }'`}
				</CodeBlock>

				<CodeBlock language="json" title="Response">
					{`{
  "success": true,
  "data": {
    "sessionId": "sess_qs_abc123xyz789",
    "sessionToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJzZXNzX3FzX2FiYzEyM3h5ejc4OSIsImF1ZCI6Im9wX2RlbW8iLCJwaWQiOiJxdWlja3N0YXJ0X3BsYXllcl8wMDEiLCJjdXIiOiJVU0QiLCJ3bSI6ImludGVncmF0ZWQiLCJleHAiOjE3MDQwNjcyMDB9.xxxxx",
    "gameUrl": "https://play.rngfort.com/play/dice?token=eyJhbGciOiJIUzI1NiIs...",
    "expiresAt": 1704067200
  }
}`}
				</CodeBlock>

				<p>
					The response includes several important fields:
				</p>
				<ul>
					<li>
						<code>sessionId</code> - Unique identifier for this session
					</li>
					<li>
						<code>sessionToken</code> - JWT token for making game API calls
					</li>
					<li>
						<code>gameUrl</code> - Full URL to load the game client with
						authentication
					</li>
					<li>
						<code>expiresAt</code> - Unix timestamp when the session expires
					</li>
				</ul>
			</Step>

			<Step number={3} title="Load the Game URL">
				<p>
					Open the <code>gameUrl</code> from the response in a browser or embed
					it in an iframe on your site. The game client will automatically
					authenticate the player using the token in the URL.
				</p>

				<CodeBlock language="typescript" title="Embedding in an iframe">
					{`// In your frontend application
const gameUrl = response.data.gameUrl;

// Option 1: Open in a new window
window.open(gameUrl, '_blank');

// Option 2: Embed in an iframe
const iframe = document.createElement('iframe');
iframe.src = gameUrl;
iframe.style.width = '100%';
iframe.style.height = '600px';
iframe.style.border = 'none';
document.getElementById('game-container').appendChild(iframe);`}
				</CodeBlock>

				<CodeBlock language="typescript" title="React component example">
					{`function GameEmbed({ gameUrl }: { gameUrl: string }) {
  return (
    <iframe
      src={gameUrl}
      style={{
        width: '100%',
        height: '600px',
        border: 'none',
        borderRadius: '8px',
      }}
      allow="fullscreen"
      title="Ringfort Game"
    />
  );
}`}
				</CodeBlock>
			</Step>

			<Step number={4} title="Place a Test Bet">
				<p>
					With the session created, you can place bets using the session token.
					Here's an example of placing a dice bet through the API:
				</p>

				<CodeBlock language="bash" title="Place a dice bet">
					{`curl -X POST https://api.rngfort.com/api/v1/game/dice/roll \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "1000",
    "target": 50.00,
    "condition": "under"
  }'`}
				</CodeBlock>

				<CodeBlock language="json" title="Response">
					{`{
  "success": true,
  "data": {
    "betId": "bet_dice_xyz789abc",
    "roll": 42.35,
    "target": 50.00,
    "condition": "under",
    "won": true,
    "amount": "1000",
    "payout": "1980",
    "multiplier": 1.98,
    "balance": "10980",
    "provablyFair": {
      "serverSeedHash": "a1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456",
      "clientSeed": "default_client_seed",
      "nonce": 1
    }
  }
}`}
				</CodeBlock>

				<p>
					The bet was successful. Let's break down what happened:
				</p>
				<ul>
					<li>
						<strong>Bet amount:</strong> 1000 cents ($10.00)
					</li>
					<li>
						<strong>Target:</strong> Under 50.00 (49.5% win chance)
					</li>
					<li>
						<strong>Roll result:</strong> 42.35 - which is under 50, so the bet
						won
					</li>
					<li>
						<strong>Payout:</strong> 1980 cents ($19.80) at 1.98x multiplier
					</li>
					<li>
						<strong>New balance:</strong> 10980 cents ($109.80)
					</li>
				</ul>
			</Step>

			<Step number={5} title="Verify the Outcome">
				<p>
					Every bet includes provably fair data that can be independently
					verified. Use the verify endpoint to confirm the bet outcome was fair:
				</p>

				<CodeBlock language="bash" title="Verify the bet">
					{`curl https://api.rngfort.com/api/v1/game/dice/verify/bet_dice_xyz789abc \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..."`}
				</CodeBlock>

				<CodeBlock language="json" title="Response">
					{`{
  "success": true,
  "data": {
    "betId": "bet_dice_xyz789abc",
    "serverSeed": "revealed_seed_after_rotation_abc123def456",
    "serverSeedHash": "a1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456",
    "clientSeed": "default_client_seed",
    "nonce": 1,
    "roll": 42.35,
    "verified": true
  }
}`}
				</CodeBlock>

				<Alert variant="success" title="Verification Complete">
					The <code>verified: true</code> field confirms that the roll was
					calculated correctly using the provably fair algorithm. Players can
					independently verify this using the HMAC-SHA256 formula.
				</Alert>

				<CodeBlock language="javascript" title="Client-side verification">
					{`import { createHmac } from 'crypto';

function verifyDiceRoll(serverSeed, clientSeed, nonce) {
  const hash = createHmac('sha256', serverSeed)
    .update(\`\${clientSeed}:\${nonce}\`)
    .digest('hex');

  const roll = parseInt(hash.slice(0, 8), 16) % 10001;
  return roll / 100; // Returns 0.00 - 100.00
}

// Example verification
const calculatedRoll = verifyDiceRoll(
  'revealed_seed_after_rotation_abc123def456',
  'default_client_seed',
  1
);
console.log(calculatedRoll); // 42.35`}
				</CodeBlock>
			</Step>

			<h2>Complete Working Example</h2>

			<p>
				Here's a complete Node.js script that demonstrates the full flow from
				session creation to bet verification:
			</p>

			<CodeBlock language="javascript" title="complete-example.js" showLineNumbers>
				{`const API_BASE = 'https://api.rngfort.com';
const SECRET_KEY = 'sk_test_demosecret001';

async function main() {
  // Step 1: Create a player session
  console.log('Creating player session...');

  const sessionResponse = await fetch(\`\${API_BASE}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${SECRET_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId: 'quickstart_player_001',
      currency: 'USD',
      walletMode: 'integrated',
    }),
  });

  const session = await sessionResponse.json();
  console.log('Session created:', session.data.sessionId);
  console.log('Game URL:', session.data.gameUrl);

  const sessionToken = session.data.sessionToken;

  // Step 2: Place a dice bet
  console.log('\\nPlacing dice bet...');

  const betResponse = await fetch(\`\${API_BASE}/api/v1/game/dice/roll\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${sessionToken}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      amount: '1000',
      target: 50.00,
      condition: 'under',
    }),
  });

  const bet = await betResponse.json();
  console.log('Roll:', bet.data.roll);
  console.log('Won:', bet.data.won);
  console.log('Payout:', bet.data.payout);
  console.log('New balance:', bet.data.balance);

  // Step 3: Verify the bet (after seed rotation)
  console.log('\\nBet placed successfully!');
  console.log('Bet ID:', bet.data.betId);
  console.log('Server Seed Hash:', bet.data.provablyFair.serverSeedHash);

  // Note: In production, verification happens after seed rotation
  // when the server seed is revealed
}

main().catch(console.error);`}
			</CodeBlock>

			<div className={styles.nextSteps}>
				<h2>What's Next?</h2>

				<p>
					You've successfully created a session, placed a bet, and verified the
					outcome. Here are some next steps to continue your integration:
				</p>

				<CardGrid columns={2}>
					<Card
						title="Authentication"
						description="Learn about API key formats and security best practices."
						href="/docs/authentication"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
								<path d="M7 11V7a5 5 0 0 1 10 0v4" />
							</svg>
						}
					/>

					<Card
						title="Wallet Integration"
						description="Set up seamless wallet callbacks or use the integrated wallet."
						href="/docs/wallet-modes"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
								<line x1="1" y1="10" x2="23" y2="10" />
							</svg>
						}
					/>

					<Card
						title="Dice Game API"
						description="Explore all dice game endpoints and configuration options."
						href="/docs/games-dice"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<rect x="4" y="4" width="16" height="16" rx="2" />
								<circle cx="9" cy="9" r="1" fill="currentColor" />
								<circle cx="15" cy="15" r="1" fill="currentColor" />
							</svg>
						}
					/>

					<Card
						title="Provably Fair"
						description="Deep dive into the provably fair algorithm and verification."
						href="/docs/provably-fair"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
							</svg>
						}
					/>
				</CardGrid>

				<Alert variant="info" title="Need Help?">
					If you run into any issues or have questions, check our{' '}
					<a href="/docs/errors">error reference</a> or contact our support team
					at <code>support@rngfort.com</code>.
				</Alert>
			</div>
		</div>
	);
}
